import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { SuggestionDataMap } from 'components/WorkspaceEditor/SuggestionStore'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types'
import { removeTextElementById } from 'components/WorkspaceEditor/utils/contentItemViewNodesUtils'
import { TrackedChange } from 'models/changes'
import { Action } from '../types/types'

/**
 * Flattens nested content elements into a mapping.
 * The key includes the parent content ID if requested.
 *
 * @param {Partial<ContentItemViewNodesType>[]} contentItemViewNode - The content item view nodes to process.
 * @param {boolean} [shouldAddParentContentId=false] - Whether to include the parent content ID in the mapping keys.
 * @returns {Record<string, Partial<TextContentElement>>} - A mapping of content item view node IDs to text content elements.
 */
export const getContentItemViewNodeElementMapping = (
    contentItemViewNode: Partial<ContentItemViewNodesType>[],
    shouldAddParentContentId: boolean = false
): Record<string, Partial<TextContentElement>> => {
    const mapping: Record<string, Partial<TextContentElement>> = {}
    for (const item of contentItemViewNode) {
        if (item?.elements) {
            for (const element of item.elements) {
                const baseKey = `${item.id}_${element.id}`
                mapping[baseKey] = element
                // Recursively process nested elements
                processNestedElements(
                    mapping,
                    item.id,
                    element,
                    shouldAddParentContentId ? item.id : undefined
                )
            }
        }
    }
    return mapping
}

/**
 * Recursively adds nested elements into the flat mapping.
 *
 * @param {Record<string, Partial<TextContentElement>>} acc - The accumulator for the mapping.
 * @param {string | undefined} parentId - The ID of the parent content item.
 * @param {Partial<TextContentElement>} element - The current text content element to process.
 * @param {string | undefined} [parentContentId=undefined] - The ID of the parent content item to include in the mapping keys.
 */
const processNestedElements = (
    acc: Record<string, Partial<TextContentElement>>,
    parentId: string | undefined,
    element: Partial<TextContentElement>,
    parentContentId: string | undefined = undefined
): void => {
    if (!Array.isArray(element.elements)) return // early return
    for (const nested of element.elements) {
        const key = `${parentContentId ? `${parentContentId}_` : ''}${
            element.id
        }_${nested.id}`
        acc[key] = nested
        // Recursively process deeper levels of nested elements
        processNestedElements(acc, parentId, nested, parentContentId)
    }
}

/**
 * Retrieves track changes data for a specific suggestion ID.
 *
 * @param {string} suggestionId - The ID of the suggestion to retrieve track changes data for.
 * @param {Record<string, TrackedChange>} trackChangesData - The track changes data to search.
 * @returns {TrackedChange | undefined} - The track changes data for the specified suggestion ID, or undefined if not found.
 */
export const getTrackChangesDataById = (
    suggestionId: string,
    trackChangesData: Record<string, TrackedChange>
): TrackedChange | undefined => {
    return Object.values(trackChangesData).find((item) =>
        item.contentItemElementId.includes(suggestionId)
    )
}

/**
 * Processes non-sibling elements for track changes and updates the content item view nodes.
 *
 * @param {Partial<ContentItemViewNodesType>[]} contentItemViewNodes - The content item view nodes to process.
 * @param {string[]} ids - The IDs of the elements to process.
 * @param {Action} action - The action to perform (accept, discard, etc.).
 * @param {(elementId: string, text: string, contentItemViewNodes: Partial<ContentItemViewNodesType>[]) => Partial<ContentItemViewNodesType>[]} updateText - A function to update the text of an element.
 * @returns {{ updatedContentItem: Partial<ContentItemViewNodesType>[]; nonSiblingElements: Record<string, Partial<TextContentElement>> }} - The updated content item view nodes and the non-sibling elements.
 */
export const processNonSiblingElementsForTrackChanges = (
    contentItemViewNodes: Partial<ContentItemViewNodesType>[],
    ids: string[],
    action: Action
): {
    updatedContentItem: Partial<ContentItemViewNodesType>[]
    nonSiblingElements: Record<string, Partial<TextContentElement>>
} => {
    const elementMapping = getContentItemViewNodeElementMapping(
        contentItemViewNodes
    )
    const elements = Object.keys(elementMapping).filter((key) =>
        ids.includes(key.split('_')[1])
    )

    const nonSiblingElements: Record<string, Partial<TextContentElement>> = {}

    elements.forEach((element) => {
        const [parentId, elementId] = element.split('_')
        const siblingKeys = Object.keys(elementMapping).filter(
            (key) => key.includes(parentId) && !key.includes(elementId)
        )
        if (!siblingKeys.length) {
            nonSiblingElements[elementId] = elementMapping[element]
        }
    })

    let updatedContentItem = [...contentItemViewNodes]
    Object.entries(nonSiblingElements).map(([key, textElement]) => {
        textElement.changeType =
            action === Action.accept || Action.acceptAll
                ? null
                : textElement.changeType
        if (action === Action.discard || action === Action.discardAll) {
            updatedContentItem = [
                ...removeTextElementById(
                    updatedContentItem,
                    textElement.id as string
                ),
            ]
        } else {
            updatedContentItem = [
                ...ModifyTextElementById(
                    textElement.id as string,
                    updatedContentItem
                ),
            ]
        }
    })

    return {
        updatedContentItem,
        nonSiblingElements,
    }
}

/**
 * Updates the text content of a node in the content item view nodes.
 * @param nodeId - The ID of the node to update.
 * @param nodes - The array of content item view nodes.
 * @returns The updated array of content item view nodes.
 */
const ModifyTextElementById = (
    nodeId: string = '',
    nodes: Partial<ContentItemViewNodesType>[]
): Partial<ContentItemViewNodesType>[] => {
    //here find the node by id and made change type null
    return nodes.map((node) => {
        if (node.id === nodeId) {
            return {
                ...node,
                changeType: null,
            }
        } else if (node.elements) {
            return {
                ...node,
                elements: ModifyTextElementById(nodeId, node.elements),
            }
        }
        return node
    })
}

/**
 * Utility function to group elements by their parent ID.
 * This reduces the number of iterations needed to find siblings.
 * @param elementMapping 
 * @returns Map<string, {
    key: string;
    element: Partial<TextContentElement>;
}[]>
 */
export const groupElementsByParent = (
    elementMapping: Record<string, Partial<TextContentElement>>
) => {
    const parentGroups = new Map<
        string,
        Array<{ key: string; element: Partial<TextContentElement> }>
    >()
    for (const [key, element] of Object.entries(elementMapping)) {
        const parentId = key.split('_')[0]
        if (!parentGroups.has(parentId)) {
            parentGroups.set(parentId, [])
        }
        parentGroups.get(parentId)!.push({ key, element })
    }
    return parentGroups
}

/**
 * Utility function to map suggestion IDs to their corresponding keys in the element mapping.
 * This reduces the number of lookups needed when processing suggestions.
 * @param elementMapping
 * @param suggestionsDataMap
 * @returns Map<string, string>
 */
export const mapSuggestionIdToKey = (
    elementMapping: Record<string, Partial<TextContentElement>>,
    suggestionsDataMap: Record<string, SuggestionDataMap>
) => {
    const suggestionToKeyMap = new Map<string, string>()
    for (const key of Object.keys(elementMapping)) {
        const suggestionId = key.split('_')[1]
        if (suggestionId && suggestionsDataMap[suggestionId]) {
            suggestionToKeyMap.set(suggestionId, key)
        }
    }
    return suggestionToKeyMap
}
